import {
  DocumentNode,
  OperationDefinitionNode,
  SelectionNode,
  VariableDefinitionNode,
} from 'graphql'

import { CartFragmentDoc } from '../../../../graphql/magento'

/**
 * Alias for the Nth removal field. Aliases are required because a mutation may not repeat the same
 * response key, and they are what let one request carry N removals.
 */
const removalAlias = (index: number): string => `remove${index}`

const itemIdVariable = (index: number): string => `itemId${index}`

const variableDefinition = (name: string, type: string): VariableDefinitionNode => ({
  kind: 'VariableDefinition',
  variable: { kind: 'Variable', name: { kind: 'Name', value: name } },
  type: {
    kind: 'NonNullType',
    type: { kind: 'NamedType', name: { kind: 'Name', value: type } },
  },
})

/**
 * One `removeItemFromCart` field. Only the final removal selects the whole cart: Magento resolves
 * cart fields in the order written and does not compute totals until `prices` runs, so selecting the
 * full cart on an earlier field would serialize (and pay for) totals that the next removal
 * immediately invalidates. See the field-order rule in `www/AGENTS.md`.
 */
const removalField = (index: number, selectWholeCart: boolean): SelectionNode => ({
  kind: 'Field',
  alias: { kind: 'Name', value: removalAlias(index) },
  name: { kind: 'Name', value: 'removeItemFromCart' },
  arguments: [
    {
      kind: 'Argument',
      name: { kind: 'Name', value: 'input' },
      value: {
        kind: 'ObjectValue',
        fields: [
          {
            kind: 'ObjectField',
            name: { kind: 'Name', value: 'cart_id' },
            value: { kind: 'Variable', name: { kind: 'Name', value: 'cartId' } },
          },
          {
            kind: 'ObjectField',
            name: { kind: 'Name', value: 'cart_item_id' },
            value: {
              kind: 'Variable',
              name: { kind: 'Name', value: itemIdVariable(index) },
            },
          },
        ],
      },
    },
  ],
  selectionSet: {
    kind: 'SelectionSet',
    selections: [
      {
        kind: 'Field',
        name: { kind: 'Name', value: 'cart' },
        selectionSet: {
          kind: 'SelectionSet',
          selections: selectWholeCart
            ? [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'cart' } }]
            : [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
        },
      },
    ],
  },
})

/**
 * Build a mutation carrying one aliased `removeItemFromCart` field per item, so N removals cost one
 * request instead of N. The GraphQL spec requires root mutation fields to execute **serially**,
 * which preserves the invariant the sequential implementation relied on: parallel mutations against
 * the same Magento cart race server-side.
 *
 * Built at runtime because the arity is dynamic, which codegen cannot express — the one hand-built
 * document in the codebase. It goes away once Magento's `removeItemsFromCart` batch mutation
 * (`DesignsForHealth_CartMutationsGraphQl`) reaches production, which removes N line items in a
 * single quote save rather than N.
 *
 * Variables are `cartId` plus `itemId0…itemId{n-1}`; see {@link removeCartItemsVariables}.
 */
export const buildRemoveCartItemsDocument = (itemCount: number): DocumentNode => {
  const indices = Array.from({ length: itemCount }, (_, index) => index)
  const operation: OperationDefinitionNode = {
    kind: 'OperationDefinition',
    operation: 'mutation',
    name: { kind: 'Name', value: 'removeCartItems' },
    variableDefinitions: [
      variableDefinition('cartId', 'String'),
      ...indices.map((index) => variableDefinition(itemIdVariable(index), 'Int')),
    ],
    selectionSet: {
      kind: 'SelectionSet',
      selections: indices.map((index) => removalField(index, index === itemCount - 1)),
    },
  }

  return {
    kind: 'Document',
    // `cart.fragment.graphql` spreads no other fragment, so its single definition is all the
    // operation needs. Reusing the codegen document keeps the fragment itself generated.
    definitions: [operation, ...CartFragmentDoc.definitions],
  }
}

/** Variables matching {@link buildRemoveCartItemsDocument} for the same item order. */
export const removeCartItemsVariables = (
  cartId: string,
  items: ReadonlyArray<{ id: string }>,
): Record<string, string | number> =>
  items.reduce<Record<string, string | number>>(
    (acc, item, index) => {
      acc[itemIdVariable(index)] = Number(item.id)
      return acc
    },
    { cartId },
  )
